/**
 * ControlRow — horizontal layout with icon, title/subtitle, and action slot.
 *
 * Used in alarm controls for rows like "Armed | Scheduled | [Disarm]"
 * and "Next change at 6am | Scheduled | [Adjust]".
 */
import { memo, type FC, type ReactNode } from "react";
import "../../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet } from "react-native-unistyles";
import { View } from "../View";
import { Text, type TextSize } from "../Text";
import type { AppColorPalette, AppColorPaletteShades } from "../../theme";

type TextColorPalette = AppColorPalette | AppColorPaletteShades;

export interface ControlRowProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitle?: string;
  readonly action?: ReactNode;
  readonly titleTextStyle?: TextSize;
  readonly subtitleTextStyle?: TextSize;
  readonly titleColor?: TextColorPalette;
  readonly subtitleColor?: TextColorPalette;
  /**
   * Optional line-height overrides for the title / subtitle Text. The
   * `TextSize` token already specifies a lineHeight (e.g. `md/medium` =
   * 24px) but that's tuned for body copy; tightening it visually
   * reduces the gap between title and subtitle without changing the
   * font size or weight. Useful when the row sits in a dense card and
   * the default lineHeight feels airy.
   *
   * Both default to whatever the textStyle token provides.
   */
  readonly titleLineHeight?: number;
  readonly subtitleLineHeight?: number;
  /**
   * Where the leading icon (and trailing action) sit relative to the
   * title/subtitle stack.
   *
   * - `"center"` (default) — both vertically centred on the full row,
   *   matching the long-standing legacy behaviour. Looks balanced when
   *   icon and action have similar heights to the text stack.
   * - `"title"` — top-align the row and rely on the caller to size the
   *   icon container so its visual centre lands on the title's centre.
   *   In practice that means a tall icon (e.g. a 32px CircularIcon) gets
   *   pulled up via a negative `marginTop` so it doesn't appear pushed
   *   down by the subtitle. The textStack inherits the row's
   *   `alignItems: "flex-start"`.
   *
   * Kept additive (no behaviour change for existing callers) so this can
   * land without touching the rest of the codebase.
   */
  readonly iconAlign?: "center" | "title";
}

export const ControlRow: FC<ControlRowProps> = memo(
  ({
    icon,
    title,
    subtitle,
    action,
    titleTextStyle = "sm/regular",
    subtitleTextStyle = "xxs/regular",
    titleColor = "text",
    subtitleColor = "textMuted",
    titleLineHeight,
    subtitleLineHeight,
    iconAlign = "center",
  }) => {
    const rowStyle = iconAlign === "title" ? styles.rowTitleAligned : styles.row;
    const titleStyle = titleLineHeight !== undefined ? [styles.text, { lineHeight: titleLineHeight }] : styles.text;
    const subtitleStyle =
      subtitleLineHeight !== undefined ? [styles.text, { lineHeight: subtitleLineHeight }] : styles.text;
    return (
      <View style={rowStyle}>
        <View style={styles.leftContent}>
          <View style={styles.icon}>{icon}</View>
          <View style={styles.textStack}>
            <Text textStyle={titleTextStyle} color={titleColor} style={titleStyle}>
              {title}
            </Text>
            {!!subtitle && (
              <Text textStyle={subtitleTextStyle} color={subtitleColor} style={subtitleStyle}>
                {subtitle}
              </Text>
            )}
          </View>
        </View>
        {action != null && <View style={styles.action}>{action}</View>}
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  // `iconAlign="title"` variant — pin children to the row's top edge so
  // the icon sits with the title rather than being pushed down by a
  // subtitle. The icon (and action) are rendered with their natural
  // heights centred on the *title* line because the row no longer stretches.
  rowTitleAligned: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  leftContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  icon: {
    alignItems: "center",
    justifyContent: "center",
  },
  textStack: {
    flexShrink: 1,
    minWidth: 0,
  },
  text: {
    flexShrink: 1,
  },
  action: {
    flexShrink: 0,
    marginLeft: theme.spacing.lg,
    // Always vertically centre the action on the full row, regardless of
    // the row's `alignItems` (which may be `flex-start` when `iconAlign=
    // "title"`). This ensures action buttons visually bracket the
    // title-and-subtitle stack rather than top-aligning.
    alignSelf: "center",
  },
}));
